import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useTree } from '@headless-tree/react';
import { syncDataLoaderFeature, dragAndDropFeature, createOnDropHandler } from '@headless-tree/core';
import type { HelpCenterStructureNode } from '@/types';
import { useOpenHelpCenter } from '../../../../hooks/useOpenHelpCenter';
import { buildFlatData } from './structureTreeUtils';
import { getNodePinRef, pinRefKey } from '../../../../libs/homepage-pins-utils';
import { useTreeExpandedState } from './useTreeExpandedState';
import { StructureTreeItem } from './StructureTreeItem';

interface StructureTreeProps {
  structure: HelpCenterStructureNode[];
  onReorderCategories: (updates: Array<{ category_id: number; parent_id: number | null; order: number }>) => void;
  onReorderItems: (updates: Array<{ item_id: number; category_id: number | null; order: number }>) => void;
  onAddCategory: (parentId: number | null) => void;
  onEditCategory: (category: any) => void;
  onDeleteNode: (node: HelpCenterStructureNode) => void;
  onAddArticle?: (categoryId: number | null) => void;
  onAddGuide?: (categoryId: number | null) => void;
  onEditItem?: (item: any) => void;
  onTogglePin?: (node: HelpCenterStructureNode) => void;
  pinnedPinKeys?: Set<string>;
  helpCenterId: number;
  helpCenterSlug: string;
  expandedCategoryIdsRef?: React.MutableRefObject<Set<string>>;
}

export function StructureTree({
  structure,
  onReorderCategories,
  onReorderItems,
  onAddCategory,
  onEditCategory,
  onDeleteNode,
  onAddArticle,
  onAddGuide,
  onEditItem,
  onTogglePin,
  pinnedPinKeys,
  helpCenterId,
  helpCenterSlug,
  expandedCategoryIdsRef,
}: StructureTreeProps) {
  const { openHelpCenter } = useOpenHelpCenter();
  const { id } = useParams<{ id: string }>();

  // Convert nested structure to flat map
  const flatData = useMemo(() => buildFlatData(structure), [structure]);

  // Manage expanded state with localStorage persistence
  const { expandedItems, setExpandedItems } = useTreeExpandedState({
    structure,
    helpCenterId,
    expandedCategoryIdsRef,
  });

  // Mutable data store for the library (like the example's `data` object)
  const childrenMapRef = React.useRef<Record<string, string[]>>({});
  const draggedItemIdRef = React.useRef<string | null>(null);

  // Sync flatData to mutable ref whenever it changes
  React.useEffect(() => {
    const map: Record<string, string[]> = { root: flatData.rootIds };
    flatData.map.forEach((data, id) => {
      map[id] = data.childrenIds;
    });
    childrenMapRef.current = map;
  }, [flatData]);

  // Handle toggle expand
  const handleToggleExpand = React.useCallback(
    (itemId: string, isExpanded: boolean) => {
      if (isExpanded) {
        setExpandedItems((prev) => prev.filter((id) => id !== itemId));
      } else {
        setExpandedItems((prev) => [...prev, itemId]);
      }
    },
    [setExpandedItems]
  );

  const tree = useTree({
    rootItemId: 'root',
    canReorder: true,
    indent: 24,
    getItemName: (item) => {
      const data = flatData.map.get(item.getId());
      if (!data) return '';
      const { node } = data;
      if (node.type === 'category') return node.category!.title;
      return node.item!.item_type === 'article'
        ? node.item!.article?.title || ''
        : node.item!.guide?.title || '';
    },
    isItemFolder: (item) => flatData.map.get(item.getId())?.node.type === 'category' || false,
    dataLoader: {
      getItem: (itemId) =>
        itemId === 'root'
          ? { type: 'root', childrenIds: flatData.rootIds }
          : flatData.map.get(itemId) || null,
      getChildren: (itemId) => childrenMapRef.current[itemId] || [],
    },
    features: [syncDataLoaderFeature, dragAndDropFeature],
    setExpandedItems,
    state: { expandedItems },
    onDrop: (items, target) => {
      draggedItemIdRef.current = items[0].getId();
      return createOnDropHandler((parentItem, newChildrenIds) => {
        // Update mutable data
        childrenMapRef.current[parentItem.getId()] = newChildrenIds;

        // Only sync to API on insertion (when dragged item is in newChildrenIds)
        const draggedId = draggedItemIdRef.current;
        if (!draggedId || !newChildrenIds.includes(draggedId)) return;

        const parentId = parentItem.getId();
        const isRoot = parentId === 'root';
        const parentData = isRoot ? null : flatData.map.get(parentId);
        const parentCategoryId = parentData?.node.type === 'category' ? parentData.node.category!.id : null;

        const categoryUpdates: Array<{ category_id: number; parent_id: number | null; order: number }> = [];
        const itemUpdates: Array<{ item_id: number; category_id: number | null; order: number }> = [];

        newChildrenIds.forEach((childId, index) => {
          const childData = flatData.map.get(childId);
          if (!childData) return;
          const node = childData.node;

          if (node.type === 'category') {
            categoryUpdates.push({ category_id: node.category!.id, parent_id: parentCategoryId, order: index });
          } else {
            itemUpdates.push({ item_id: node.item!.id, category_id: parentCategoryId, order: index });
          }
        });

        if (categoryUpdates.length > 0) onReorderCategories(categoryUpdates);
        if (itemUpdates.length > 0) onReorderItems(itemUpdates);
        draggedItemIdRef.current = null;
      })(items, target);
    },
  });

  // Get drag and drop state
  const treeState = tree.getState();
  const dndState = treeState.dnd;

  return (
    <div {...tree.getContainerProps()} className="space-y-1 relative">
      {tree.getItems().map((item) => {
        const itemId = item.getId();
        if (itemId === 'root') return null;

        const data = flatData.map.get(itemId);
        if (!data) return null;

        const isDragging = dndState?.draggedItems?.some((dragged: any) => dragged.getId() === itemId) || false;
        const isDragOver = dndState?.draggingOverItem?.getId() === itemId || false;

        return (
          <StructureTreeItem
            key={itemId}
            itemId={itemId}
            node={data.node}
            childrenIds={data.childrenIds}
            level={item.getItemMeta().level}
            isExpanded={item.isExpanded()}
            isDragging={isDragging}
            isDragOver={isDragOver}
            itemProps={item.getProps()}
            helpCenterId={id || ''}
            helpCenterSlug={helpCenterSlug}
            flatData={flatData}
            onToggleExpand={handleToggleExpand}
            onOpenHelpCenter={openHelpCenter}
            onAddCategory={onAddCategory}
            onEditCategory={onEditCategory}
            onDeleteNode={onDeleteNode}
            onAddArticle={onAddArticle}
            onAddGuide={onAddGuide}
            onEditItem={onEditItem}
            onTogglePin={onTogglePin}
            isPinned={
              onTogglePin && pinnedPinKeys
                ? (() => {
                    const ref = getNodePinRef(data.node);
                    return ref ? pinnedPinKeys.has(pinRefKey(ref)) : false;
                  })()
                : false
            }
          />
        );
      })}
      {/* Drag line indicator */}
      <div
        style={tree.getDragLineStyle()}
        className="absolute left-0 right-0 h-0.5 bg-primary z-50 pointer-events-none"
      />
    </div>
  );
}
